import Image from "next/image";
import { Lock } from "lucide-react";

import { cn } from "@/lib/utils";
import {
	getPaymentBrands,
	type PaymentBrandDisplay,
	type PaymentBrandId,
} from "@/config/payment-brand-display";

interface PaymentLogo {
	id: PaymentBrandId;
	src: string;
	compactSrc?: string;
	alt: string;
	width: number;
	height: number;
}

// Which logos appear follows getPaymentBrands, i.e. what the checkout renders.
// PayPal is not offered as a payment method and has no logo here.
//
// Points at the tightly-cropped SVGs (Task 9, 2026-08-22), not the original
// PNGs. Those PNGs are 1024x1024 canvases where the wordmark itself only fills
// a fraction of the frame, so at this strip's 14-16px row height the mark was
// only a few pixels tall and read as blurry - the exact example the task that
// added these SVGs was justified by. Intrinsic dimensions below exactly match
// each SVG's viewBox so aspect ratios are never rounded. CSS sets the visible
// 14/16px height (see public/payment/README.md for what each crop keeps and drops).
//
// Regression 2026-08-22..09-03 (commit c4519058a): those SVG roots only carry
// a `viewBox`, no pixel `width`/`height` - no intrinsic size at all. Rendering
// them with `h-auto w-auto` (as this component did) overrides the HTML
// width/height attributes with nothing to fall back on, so the browser
// resolved every logo's width to 0px: five empty bordered chips in
// production. Fixed by giving the SVG roots real width/height (see the
// README) and rendering with a fixed height class instead of h-auto/w-auto.
const LOGOS: PaymentLogo[] = [
	{ id: "visa", src: "/payment/visa.svg", alt: "Visa", width: 320, height: 105 },
	{ id: "mastercard", src: "/payment/mastercard.svg", alt: "Mastercard", width: 320, height: 196 },
	{
		id: "amex",
		src: "/payment/amex.svg",
		compactSrc: "/payment/amex.svg",
		alt: "American Express",
		width: 45,
		height: 45,
	},
	{ id: "applePay", src: "/payment/applepay.svg", alt: "Apple Pay", width: 320, height: 138 },
	{ id: "googlePay", src: "/payment/googlepay.svg", alt: "Google Pay", width: 320, height: 129 },
	{
		id: "link",
		src: "/payment/link.svg",
		compactSrc: "/payment/link.svg",
		alt: "Link",
		width: 72,
		height: 24,
	},
	{
		id: "stripe",
		src: "/payment/stripe.svg",
		compactSrc: "/payment/stripe.svg",
		alt: "Stripe",
		width: 360,
		height: 151,
	},
	{ id: "sepa", src: "/payment/sepa.svg", alt: "SEPA", width: 320, height: 90 },
	{ id: "revolut", src: "/payment/revolut.svg", alt: "Revolut", width: 320, height: 72 },
];

interface PaymentStripProps {
	channel?: string;
	countryCode?: string;
	brands?: readonly PaymentBrandId[];
	display?: PaymentBrandDisplay;
	compact?: boolean;
	assetVariant?: "original" | "compact";
	layout?: "inline" | "grid";
	label?: string;
	className?: string;
}

export function PaymentStrip({
	channel,
	countryCode,
	brands,
	display = "default",
	compact = false,
	assetVariant = "original",
	layout = "inline",
	label,
	className,
}: PaymentStripProps) {
	const isProductDetail = display === "productDetail";
	// Legacy contexts without a channel only show brands common to both markets.
	const visibleBrands = getPaymentBrands({
		channel: channel ?? "us",
		countryCode,
		surface: "information",
		availableBrands: brands,
		display,
	});
	const logos = LOGOS.filter((logo) => visibleBrands.includes(logo.id)).map((logo) => (
		<Image
			key={logo.alt}
			src={
				assetVariant === "compact"
					? (logo.compactSrc ?? logo.src.replace(/\.svg$/, "-compact.webp"))
					: logo.src
			}
			alt={logo.alt}
			width={logo.width}
			height={logo.height}
			className={cn(
				isProductDetail
					? logo.id === "amex"
						? "h-7"
						: logo.id === "mastercard"
							? "h-6"
							: logo.id === "visa"
								? "h-4"
								: "h-5"
					: compact
						? "h-3.5"
						: "h-4",
				"w-auto object-contain",
			)}
			unoptimized
		/>
	));
	// Without an offered brand there is nothing to show: no orphaned label or lock.
	if (logos.length === 0) return null;

	if (layout === "grid") {
		return (
			<div className={cn("w-full min-w-0 @container", className)}>
				{label && <p className="mb-2 text-xs text-muted-foreground">{label}</p>}
				<ul
					aria-label={label}
					className={cn(
						isProductDetail
							? "grid grid-cols-3 items-center gap-x-2 gap-y-3 @[26rem]:grid-cols-4"
							: "flex flex-wrap items-center justify-center gap-y-2",
						!isProductDetail &&
							(logos.length === 4
								? "[&>li]:w-1/2 @[26rem]:[&>li]:w-1/4"
								: "[&>li]:w-1/3 @[26rem]:[&>li]:w-1/6"),
					)}
				>
					{logos.map((logo) => (
						<li
							key={logo.key}
							className={cn(
								"flex min-w-0 items-center justify-center px-1.5",
								isProductDetail ? "min-h-8" : "min-h-7",
							)}
						>
							{logo}
						</li>
					))}
				</ul>
			</div>
		);
	}

	return (
		<div className={cn("flex flex-wrap items-center gap-x-3 gap-y-1.5", className)}>
			{label && <p className="w-full text-xs text-muted-foreground">{label}</p>}
			{/* Deliberately frameless: since owner feedback 2026-08-22 (checkout, see
			    checkout) bordered chips read as buttons. The PDP
			    follows the same grammar here - no rounded/border/bg-card wrapper. */}
			<Lock className="h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={1.5} aria-hidden />
			{logos}
		</div>
	);
}
